import { useState, useEffect, useCallback, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { IconPlus, IconSearch, IconLayoutGrid, IconList, IconBackpack, IconTrash, IconUsers, IconAlertTriangle, IconDotsVertical, IconEye, IconPencil, IconCopy, IconStar } from '@tabler/icons-react'
import { KitCollectionForm } from './KitCollectionForm.tsx'
import { kitCollectionsApi, type KitCollection, type KitCollectionDeleteInfo } from '../../lib/api.ts'
import { useToast } from '../../components/ui/Toast.tsx'
import { InlineError } from '../../components/ui/ErrorBoundary.tsx'

type ViewMode = 'grid' | 'list'

// ── Delete confirm dialog ─────────────────────────────────────────────────────

function KitCollectionDeleteDialog({
  open, info, onConfirm, onCancel, confirming
}: {
  open: boolean
  info: KitCollectionDeleteInfo | null
  onConfirm: () => void
  onCancel: () => void
  confirming: boolean
}) {
  if (!open || !info) return null

  const isVisible = info.visibilityId && info.visibilityId !== 'private'
  const isShared = info.isShared

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-sm bg-surface-overlay border border-border-default rounded-2xl shadow-2xl p-6">
        <div className="w-10 h-10 rounded-full bg-red-950 border border-red-800 flex items-center justify-center mb-4">
          <IconTrash size={18} className="text-red-400" />
        </div>

        <h3 className="text-sm font-semibold text-zinc-100 mb-2">
          Delete "{info.bagName}"?
        </h3>

        {(isShared || isVisible) ? (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-950/30 border border-amber-900/30 mb-4">
            <IconAlertTriangle size={14} className="text-amber-400 shrink-0 mt-0.5" />
            <div className="text-xs text-amber-300 leading-relaxed">
              {isShared
                ? 'This bag is currently shared with other users. Deleting it will immediately revoke their access.'
                : 'This bag is currently visible to the community or public. Deleting it will remove it from all shared views immediately.'}
            </div>
          </div>
        ) : (
          <p className="text-sm text-zinc-400 mb-4">
            This bag and all its item references will be permanently deleted. The gear items themselves will not be affected.
          </p>
        )}

        {isShared && (
          <div className="flex items-center gap-2 mb-4 text-xs text-zinc-500">
            <IconUsers size={13} />
            <span>Shared access will be revoked for all members</span>
          </div>
        )}

        <div className="flex gap-2">
          <button onClick={onCancel} disabled={confirming}
            className="flex-1 px-4 py-2 rounded-lg text-sm border border-border-default text-zinc-400 hover:text-zinc-200 disabled:opacity-50 transition-colors">
            Cancel
          </button>
          <button onClick={onConfirm} disabled={confirming}
            className="flex-1 px-4 py-2 rounded-lg text-sm font-medium bg-red-900 text-red-200 hover:bg-red-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
            {confirming ? 'Deleting...' : 'Delete bag'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── KitCollectionsPage ──────────────────────────────────────────────────────────────

export function KitCollectionsPage({ favoritesOnly = false, filterVisibility }: { favoritesOnly?: boolean; filterVisibility?: string }) {
  const { success, error } = useToast()
  const [searchParams, setSearchParams] = useSearchParams()
  const [viewMode, setViewMode] = useState<ViewMode>('grid')
  const [search, setSearch] = useState('')
  const [formOpen, setFormOpen] = useState(false)
  const [bags, setBags] = useState<KitCollection[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)

  // View/Edit state
  const [selectedBag, setSelectedBag] = useState<KitCollection | null>(null)
  const [bagFormMode, setBagFormMode] = useState<'view' | 'edit' | 'new'>('new')
  const [bagFormOpen, setBagFormOpen] = useState(false)

  // Context menu state
  const [menuBag, setMenuBag] = useState<KitCollection | null>(null)
  const [menuPos, setMenuPos] = useState<{ x: number; y: number } | null>(null)
  const [menuAnchor, setMenuAnchor] = useState<'dots' | 'context'>('dots')
  const menuRef = useRef<HTMLDivElement>(null)

  // Delete state
  const [deleteInfo, setDeleteInfo] = useState<KitCollectionDeleteInfo | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [confirming, setConfirming] = useState(false)

  // Hover card state — wired when hover card is implemented
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [hoverItem, setHoverItem] = useState<null>(null)
  const [hoverRect, setHoverRect] = useState<DOMRect | null>(null)

  const dismissHover = () => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current)
    setHoverItem(null)
    setHoverRect(null)
  }

  const load = useCallback(async () => {
    setLoading(true)
    setLoadError(null)
    try {
      const res = await kitCollectionsApi.list()
      setBags(res.data ?? [])
    } catch (e: any) {
      setLoadError(e.message)
      error('Failed to load kit collections', e.message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  // Auto-open new bag form if ?action=new
  useEffect(() => {
    if (searchParams.get('action') === 'new') {
      setFormOpen(true)
      setSearchParams({}, { replace: true })
    }
  }, [searchParams])

  // Close context menu on outside click
  useEffect(() => {
    if (!menuBag) return
    const h = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuBag(null); setMenuPos(null)
      }
    }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [menuBag])

  const filtered = bags.filter(b => {
    if (favoritesOnly && !b.isFavorite) return false
    if (filterVisibility && b.visibility !== filterVisibility) return false
    return b.name.toLowerCase().includes(search.toLowerCase())
  })

  const handleToggleFavorite = async (bag: KitCollection) => {
    try {
      await kitCollectionsApi.toggleFavorite(bag.id)
      setBags(prev => prev.map(b => b.id === bag.id ? { ...b, isFavorite: !b.isFavorite } : b))
    } catch (e: any) { error('Failed to update favorite', e.message) }
  }

  const openNew = () => { dismissHover(); setFormOpen(true) }
  const openView = (bag: KitCollection) => { dismissHover(); setSelectedBag(bag); setBagFormMode('view'); setBagFormOpen(true); setMenuBag(null) }
  const openEdit = (bag: KitCollection) => { dismissHover(); setSelectedBag(bag); setBagFormMode('edit'); setBagFormOpen(true); setMenuBag(null) }

  const handleDuplicate = async (bag: KitCollection) => {
    setMenuBag(null)
    try {
      await kitCollectionsApi.create({ name: `${bag.name} (Copy)`, description: bag.description, visibilityId: bag.visibilityId, collectionTypeId: bag.collectionTypeId })
      success('Duplicated', `${bag.name} (Copy) created`)
      load()
    } catch (e: any) { error('Failed to duplicate', e.message) }
  }

  // Step 1: fetch delete info to populate confirm dialog
  const handleDeleteClick = async (bag: KitCollection, e: React.MouseEvent) => {
    e.stopPropagation()
    dismissHover()
    setDeletingId(bag.id)
    try {
      const res = await kitCollectionsApi.deleteInfo(bag.id)
      setDeleteInfo(res.data)
      setDeleteDialogOpen(true)
    } catch (e: any) {
      error('Failed to load bag info', e.message)
      setDeletingId(null)
    }
  }

  // Step 2: actually delete
  const handleDeleteConfirm = async () => {
    if (!deletingId) return
    setConfirming(true)
    try {
      await kitCollectionsApi.deleteConfirmed(deletingId)
      success('Kit collection deleted')
      setDeleteDialogOpen(false)
      setDeleteInfo(null)
      setDeletingId(null)
      load()
    } catch (e: any) {
      error('Delete failed', e.message)
    } finally {
      setConfirming(false)
    }
  }

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false)
    setDeleteInfo(null)
    setDeletingId(null)
  }

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex items-center gap-3 px-6 py-4 border-b border-border-default flex-wrap">
        <h1 className="text-sm font-medium text-zinc-200 flex-1">
          Kit Collections <span className="ml-2 text-zinc-500 font-normal text-xs">· {bags.length} collections</span>
        </h1>
        <div className="relative">
          <IconSearch size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search bags..."
            className="pl-7 pr-3 py-1.5 bg-surface-raised border border-border-default rounded-lg text-xs text-zinc-300 placeholder-zinc-500 outline-none focus:border-brand-orange/50 w-44" />
        </div>
        <div className="flex items-center bg-surface-raised border border-border-default rounded-lg p-0.5 gap-0.5">
          <button onClick={() => setViewMode('grid')}
            className={`p-1.5 rounded-md transition-colors ${viewMode === 'grid' ? 'bg-surface-float text-zinc-200' : 'text-zinc-500 hover:text-zinc-300'}`}>
            <IconLayoutGrid size={14} />
          </button>
          <button onClick={() => setViewMode('list')}
            className={`p-1.5 rounded-md transition-colors ${viewMode === 'list' ? 'bg-surface-float text-zinc-200' : 'text-zinc-500 hover:text-zinc-300'}`}>
            <IconList size={14} />
          </button>
        </div>
        <button onClick={openNew}
          className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-medium bg-brand-orange text-white hover:bg-brand-orange-dark transition-colors">
          <IconPlus size={14} /> Add Kit Collection
        </button>
      </div>

      {/* Context menu */}
      {menuBag && (
        <div ref={menuRef}
          style={menuPos
            ? { position: 'fixed', top: menuPos.y, left: menuPos.x, zIndex: 9999 }
            : { position: 'fixed', zIndex: 9999 }}
          className="w-44 bg-zinc-900 border border-border-strong rounded-xl shadow-2xl py-1 overflow-hidden">
          {[
            { label: 'View', icon: <IconEye size={14} />, onClick: () => openView(menuBag) },
            { label: 'Edit', icon: <IconPencil size={14} />, onClick: () => openEdit(menuBag) },
            { label: 'Duplicate', icon: <IconCopy size={14} />, onClick: () => handleDuplicate(menuBag) },
            { label: 'Delete', icon: <IconTrash size={14} />, onClick: (e: any) => { handleDeleteClick(menuBag, e); setMenuBag(null) }, danger: true },
          ].map((a, i) => (
            <button key={i} type="button" onClick={a.onClick}
              className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm transition-colors text-left ${(a as any).danger ? 'text-red-400 hover:bg-red-950/40' : 'text-zinc-300 hover:bg-surface-raised'}`}>
              {a.icon}{a.label}
            </button>
          ))}
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {loadError ? (
          <div className="p-6"><InlineError message={loadError} onRetry={load} /></div>
        ) : loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-sm text-zinc-500">Loading kit collections...</div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="w-16 h-16 rounded-2xl bg-surface-raised border border-border-default flex items-center justify-center mx-auto mb-4">
                <IconBackpack size={24} className="text-zinc-600" />
              </div>
              <h3 className="text-sm font-medium text-zinc-400 mb-1">
                {search ? 'No matching kit collections' : 'No kit collections yet'}
              </h3>
              <p className="text-xs text-zinc-500 mb-4 max-w-xs">
                {search ? 'Try a different search term.' : 'Create a kit collection to organise and share your gear.'}
              </p>
              {!search && (
                <button onClick={openNew}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-orange text-white text-xs font-medium hover:bg-brand-orange-dark transition-colors mx-auto">
                  <IconPlus size={14} /> Create your first kit collection
                </button>
              )}
            </div>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="p-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {filtered.map(bag => (
              <div key={bag.id}
                onClick={() => openView(bag)}
                onContextMenu={e => { e.preventDefault(); setMenuPos({ x: e.clientX, y: e.clientY }); setMenuBag(bag) }}
                className="relative p-4 rounded-xl bg-surface-overlay border border-border-default hover:border-border-strong transition-colors cursor-pointer group">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-zinc-700 to-zinc-800 border border-border-default flex items-center justify-center mb-3">
                  <svg viewBox="0 0 40 40" className="w-7 h-7 opacity-60" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 16h24l-2 16H10L8 16z" stroke="white" strokeWidth="1.8" strokeLinejoin="round"/>
                    <path d="M14 16v-4a6 6 0 0112 0v4" stroke="white" strokeWidth="1.8" strokeLinecap="round"/>
                    <path d="M8 21h24" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                </div>
                <div className="text-sm font-medium text-zinc-200 truncate">{bag.name}</div>
                <div className="text-xs text-zinc-500 mt-0.5 flex items-center gap-1.5">
                  {bag.itemCount} items
                  {bag.isShared && <><span>·</span><IconUsers size={10} /><span>Shared</span></>}
                </div>
                {/* ... menu — always visible */}
                <button
                  onClick={e => { e.stopPropagation(); setMenuPos(null); setMenuBag(bag) }}
                  className="absolute top-2 right-2 p-1.5 rounded-md text-zinc-500 hover:text-zinc-200 hover:bg-surface-base transition-all">
                  <IconDotsVertical size={13} />
                </button>
                {/* Favorite star */}
                <button
                  onClick={e => { e.stopPropagation(); handleToggleFavorite(bag) }}
                  className="absolute top-2 right-8 p-0.5 rounded-full transition-colors hover:scale-110">
                  <IconStar size={13}
                    className={bag.isFavorite ? 'text-brand-orange' : 'text-zinc-500 hover:text-brand-orange'}
                    fill={bag.isFavorite ? 'currentColor' : 'none'} strokeWidth={2} />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-6 space-y-2 max-w-3xl">
            {filtered.map(bag => (
              <div key={bag.id}
                onClick={() => openView(bag)}
                onContextMenu={e => { e.preventDefault(); setMenuPos({ x: e.clientX, y: e.clientY }); setMenuBag(bag) }}
                className="flex items-center gap-4 p-3 rounded-xl bg-surface-overlay border border-border-default hover:border-border-strong transition-colors cursor-pointer group">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-zinc-700 to-zinc-800 border border-border-default flex items-center justify-center shrink-0">
                  <svg viewBox="0 0 40 40" className="w-6 h-6 opacity-60" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 16h24l-2 16H10L8 16z" stroke="white" strokeWidth="1.8" strokeLinejoin="round"/>
                    <path d="M14 16v-4a6 6 0 0112 0v4" stroke="white" strokeWidth="1.8" strokeLinecap="round"/>
                    <path d="M8 21h24" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-zinc-200 truncate">{bag.name}</div>
                  <div className="text-xs text-zinc-500 mt-0.5 flex items-center gap-1.5">
                    {bag.itemCount} items
                    {bag.isShared && <><span>·</span><IconUsers size={10} /><span>Shared</span></>}
                  </div>
                </div>
                <button
                  onClick={e => { e.stopPropagation(); handleToggleFavorite(bag) }}
                  className="p-0.5 rounded-full transition-colors hover:scale-110 shrink-0">
                  <IconStar size={13}
                    className={bag.isFavorite ? 'text-brand-orange' : 'text-zinc-500 hover:text-brand-orange'}
                    fill={bag.isFavorite ? 'currentColor' : 'none'} strokeWidth={2} />
                </button>
                <button
                  onClick={e => { e.stopPropagation(); setMenuPos(null); setMenuBag(bag) }}
                  className="p-1.5 rounded-md text-zinc-500 hover:text-zinc-200 hover:bg-surface-base transition-all shrink-0">
                  <IconDotsVertical size={13} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* New bag form */}
      <KitCollectionForm open={formOpen} onClose={() => setFormOpen(false)} onSaved={load} />

      {/* View/Edit bag form */}
      {selectedBag && bagFormOpen && (
        <KitCollectionForm
          open={bagFormOpen}
          bag={selectedBag}
          mode={bagFormMode}
          onClose={() => { setBagFormOpen(false); setSelectedBag(null) }}
          onSaved={() => { load(); setBagFormOpen(false); setSelectedBag(null) }}
        />
      )}

      <KitCollectionDeleteDialog
        open={deleteDialogOpen}
        info={deleteInfo}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        confirming={confirming}
      />
    </div>
  )
}
